// The customizer's layout setup, out of CustomizerScreen (backfill PR 31), where the same
// block ran three times, at the first load, at a layout change through undo or redo and at
// a pick from the layout list: the keycap groups a layout has, its keycaps as a new design
// starts with them (white, without entries), the profile a new design on it starts with,
// its name in the layout search with the sold-out suffix, the layouts of restricted
// products, and the designs the three resets produce: a new design on a picked layout, the
// stored design put on the first layout, and the editors reset to their defaults. Since chain 8
// (Oct 2026) also the layout a design sits on and the product that sells a layout (the screen
// held seven and four copies of the lookups), and the stock gates of the cart chain: a
// profile's stock, which the quantity field's limit and the add-to-cart button read, and the
// sold-out marks of the layout search, the layout list and the profile select. Since chain 10
// (Oct 2026) also the layout search's matches.
import { deepCopy } from './deepObjects';
import { DEFAULT_DESIGN_STATE, defaultKeycapValues } from './designState';
import { formatLayoutLabel } from './layoutInfo';

export const layoutGroups = (layoutRows) => {
  const groupsInLayout = new Set(); // using a Set to avoid duplicates
  layoutRows.forEach((row) => {
    row.forEach((keycap) => {
      if (keycap.group) groupsInLayout.add(keycap.group); // if the object has a 'group' property, the value of group will be added to the Set
    });
  }); // this function checks each keycap of the selected layout and creates a set that contains all keycap groups this layout has (for example the 60% keyboard has no numbad and therefor not keycaps with the group 'numbad')
  return Array.from(groupsInLayout); // converts the Set back to an array
}; // the keycap groups of a layout, in the order of their first keycap (the colour editor offers one entry per group)

export const blankKeycapData = (layoutRows) =>
  layoutRows.map((row) =>
    row.map((keycap) => ({
      ...keycap,
      ...deepCopy(defaultKeycapValues), // deep copy of the defaultKeycapValues object
      color: '#FFFFFF', // Override the color property (of ...defaultKeycapValues)
    }))
  ); // adding the defaultKeycapValues object to each keycap of the selected layout (the layouts fetched from the data base come without the defaultKeycapValues, as they are the same for every keycap); the spacers get them too

export const firstProfileInStock = (stockQuantities) => {
  const profiles = Object.entries(stockQuantities || {});

  const firstAvailableProfile = profiles.find(([, quantity]) => quantity > 0); // Find the first profile with stock greater than 0

  return firstAvailableProfile
    ? firstAvailableProfile[0] // Use the first in stock profile
    : profiles[0]?.[0] || ''; // Default to the first profile or an empty string if no profiles exist
}; // the profile a new design on the layout starts with (the first load's version; the layout pick's copy asked for a quantity of 1 or more, the same for the whole numbers the stock calculation writes, and read the quantities without the fallback, which every layout document has)

export const layoutWithId = (layouts, layoutId) =>
  layouts.find((layout) => layout._id === layoutId); // the listed layout with the id (the screen's layout effects, which depend on the design's layout id alone)

export const layoutOfDesign = (layouts, design) =>
  layoutWithId(layouts, design.layoutEditor.id); // the layout a design sits on, undefined when the list has none with its id (no product the user can see references it any more: an admin deactivated the product, or a restricted one no longer lists the user)

export const productOfLayout = (products, layoutId) =>
  products.find((product) => product.layout === layoutId); // the keycap-set product that sells a layout (the products route lists each layout under one product); the ids are strings in both lists, as the route's JSON sends them. The screen's four copies compared through toString(), which a product without a layout (a crafted save: the product editor requires one for a keycap set) would have crashed on; here such a product matches nothing but an undefined id, which no caller passes (a design's id defaults to ''), as in layoutDisplayName's and savedDesignCartItem's copies before

export const profileStock = (layout, profile) =>
  layout?.stockQuantities?.[profile]; // the sets in stock of a layout in a profile, as the quantity field's limit reads it: undefined without a layout, for a profile the layout no longer lists and for a layout saved without keycap IDs, whose stock map is empty (KNOWN_BUGS.md, a dropped profile). A prototype key from a crafted storage (constructor) reads a function here, which every gate answers as it does an unlisted profile: left without Object.hasOwn (a chain 8 review)

export const isProfileSoldOut = (layout, profile) =>
  profileStock(layout, profile) <= 0; // whether the layout has no stock in the profile: the sold-out suffix, the add-to-cart button's Sold Out and the profile select's class. False for a profile the layout doesn't list (undefined <= 0), so such a design shows as available, and a layout without keycap IDs lists none: Known bug (KNOWN_BUGS.md, a dropped profile)

export const isLayoutSoldOut = (layout) =>
  Boolean(layout?.stockQuantities) &&
  Object.values(layout.stockQuantities).every((quantity) => quantity <= 0); // whether the layout has no stock in any profile (the layout search field's class); false without a layout, and true for a layout saved without keycap IDs, whose empty stock map has no profile in stock, while its name gets no suffix and the add-to-cart button stays enabled: Known bug (KNOWN_BUGS.md, a dropped profile)

export const layoutProfiles = (layout) =>
  Object.keys(layout?.stockQuantities || {}); // the profiles a layout lists, in the order of its stock map (the profile select's options); none without a layout

const soldOutSuffix = (layout, profile, t) =>
  isProfileSoldOut(layout, profile) ? t('customizer-screen-sold-out') : '';

export const profileOptionLabel = (layout, profile, t) =>
  `${profile}${soldOutSuffix(layout, profile, t)}`; // a profile as the profile select lists it, with the sold-out suffix when the layout has no stock in it

export const productNameOfLayout = (products, layoutId) =>
  productOfLayout(products, layoutId)?.name || '???'; // the name of the product that sells the layout, ??? when none does (the layout filters of My Designs and Community Designs held a copy each until a chain 8 review)

export const layoutDisplayName = (layout, products, profile, t) => {
  if (!layout) return '';
  return `${productNameOfLayout(products, layout._id)}${soldOutSuffix(layout, profile, t)}`;
}; // helper function to produce display name for layouts/products in the Dropdown of the search bar: the product's name, with the sold-out suffix when the layout has no stock in the design's profile

export const restrictedLayoutIds = (products) =>
  new Set(
    products
      .filter((p) => p.visibility === 'restricted' && p.layout)
      .map((p) => String(p.layout))
  ); // gather the layout IDs from all restricted products

export const designForLayout = (layoutData, keycapData) => {
  const defaults = deepCopy(DEFAULT_DESIGN_STATE); // a fresh copy: the default design state is shared by every use, its settings and metadata too (the spread below took them from the shared default and handed every new design its selection array, until a PR 31 review)

  return {
    ...defaults, // Start with a copy of the default design state
    name: layoutData.layoutName, // Update the name of the design
    keycapData, // Update keycapData
    layoutEditor: {
      id: layoutData._id, // Update the layoutEditor id
    },
    profileEditor: {
      profile: firstProfileInStock(layoutData.stockQuantities),
    },
    settings: {
      ...defaults.settings,
      isoEnterSelected: keycapData[0][0]?.isIsoEnter || false, // Update isoEnterSelected
    },
    metadata: {
      ...defaults.metadata,
      isCustomLayout: Boolean(layoutData.restricted),
    },
  };
}; // the new design a pick from the layout list starts, over the layout's blank keycaps

export const designOnLayout = (designState, layoutData, keycapData) => ({
  ...designState,
  name: layoutData.layoutName,
  keycapData,
  profileEditor: {
    ...designState.profileEditor,
    profile: firstProfileInStock(layoutData.stockQuantities),
  },
  layoutEditor: {
    ...designState.layoutEditor,
    id: layoutData._id,
  },
  metadata: {
    ...designState.metadata,
    isCustomLayout: Boolean(layoutData.restricted), // determine if the default layout is tied to a restricted product (custom layout); a boolean as designForLayout writes it (the first load's copy stored the flag raw, until a PR 31 review)
  },
}); // the design state put on the first layout at a first visit, and when the stored design's layout is gone: its editors kept, the layout's blank keycaps and name, its first profile in stock

export const designWithResetEditors = (prevDesign, layoutName, keycapData) => {
  const defaults = deepCopy(DEFAULT_DESIGN_STATE); // a fresh copy: the default design state is shared by every use
  const fieldsToReset = {
    name: layoutName,
    keycapData,
    keycapTags: defaults.keycapTags,
    keycapColorEditor: defaults.keycapColorEditor,
    textEditor: defaults.textEditor,
    iconEditor: defaults.iconEditor,
    imageEditor: defaults.imageEditor,
  }; // specifying the fields of the design state and design_state localStorage that need to be reset

  const updatedDesign = { ...prevDesign }; // Create a shallow copy of the current state

  Object.entries(fieldsToReset).forEach(([key, defaultValue]) => {
    updatedDesign[key] = defaultValue; // Replace the field with its default value
  });

  return updatedDesign; // Return the updated design state
}; // the design after the keyboard reset: the layout's blank keycaps and name, the tags and the editors at their defaults, the layout, profile, settings and metadata kept

export const searchedLayouts = (layouts, searchTerm) =>
  layouts.filter((layout) => {
    if (!searchTerm) return false; // if empty, show no filtered layouts
    const lowerTerm = searchTerm.toLowerCase();

    const nameMatches = formatLayoutLabel(layout)
      .toLowerCase()
      .includes(lowerTerm); // the layout's label as the product editor's select shows it (the screen spelled the label out until chain 10, Oct 2026, with an empty tag list as ", ,", so a term across that gap matched: user decision)

    const altNamesMatch = layout.alternativeNames?.some((alt) =>
      alt.toLowerCase().includes(lowerTerm)
    );
    const modelNamesMatch = layout.keyboardModels?.some((model) =>
      model.toLowerCase().includes(lowerTerm)
    );

    return nameMatches || altNamesMatch || modelNamesMatch;
  }); // Filtered layouts based on the layoutName, alternativeNames and keyboardModels: the layouts the customizer's search field lists for a term
